import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  Self,
  ViewContainerRef,
  inject,
  input,
  output,
} from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { MdwButton } from '@spaces-ui/meadow/components/button';
import { MdwIcon } from '@spaces-ui/meadow/icons';
import { Close_Large, Hide_Outline, Search, View_Outline } from '@spaces-ui/meadow/icons/svgs';

@Directive({
  selector:
    // eslint-disable-next-line @angular-eslint/directive-selector
    'input[type="text"], input[type="number"], input[type="password"], input[type="search"], textarea',
  standalone: true,
})
export class MdwInput implements OnInit, OnDestroy {
  private element = inject(ElementRef);
  private renderer = inject(Renderer2);
  private viewContainerRef = inject(ViewContainerRef);

  public label = input<string>();
  public description = input<string>();
  public customErrorMessages = input<{ [key: string]: string }>({});
  public clearStyle = input<'default' | 'text'>('default');

  public clear = output<void>();

  private control!: AbstractControl;
  private statusSubscription!: Subscription;
  private inputError!: HTMLDivElement;
  private nativeElement!: HTMLInputElement;
  private parentElement!: HTMLElement;
  // Nodes created imperatively in ngOnInit and inserted into parentElement.
  // They live outside Angular's view tree, so they must be torn down manually.
  private container?: HTMLElement;
  private createdLabel?: HTMLElement;
  private createdDescription?: HTMLElement;
  private unlistenInput?: () => void;
  private valueChangesSub?: Subscription;
  private teardownValuePatch?: () => void;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  public constructor(@Optional() @Self() private ngControl: NgControl) {}

  ngOnInit() {
    this.nativeElement = this.element.nativeElement;
    this.parentElement = this.renderer.parentNode(this.nativeElement);
    const inputType = this.nativeElement.attributes.getNamedItem('type')?.value;

    const labelString = this.label();
    const descriptionString = this.description();

    // helper used everywhere
    const updateHasValue = (container?: HTMLElement) => {
      const hasValue = (this.nativeElement.value ?? '').length > 0;
      if (container) {
        container.classList.toggle('search-has-value', hasValue);
      }
    };

    // Create the label element
    if (labelString) {
      const label = this.renderer.createElement('label') as HTMLElement;
      if (!descriptionString) {
        label.classList.add('no-description');
      }
      this.renderer.setAttribute(label, 'for', this.nativeElement.id);
      const labelText = this.renderer.createText(labelString);
      this.renderer.appendChild(label, labelText);
      this.renderer.insertBefore(this.parentElement, label, this.nativeElement);
      this.createdLabel = label;
    }

    // Create the description element
    if (descriptionString) {
      const description = this.renderer.createElement('div');
      this.renderer.addClass(description, 'input-description');
      const descriptionText = this.renderer.createText(descriptionString);
      this.renderer.appendChild(description, descriptionText);
      this.renderer.insertBefore(this.parentElement, description, this.nativeElement);
      this.createdDescription = description;
    }

    switch (inputType) {
      case 'search': {
        // surround the input with a container div and add a search icon (svg)
        const container = this.renderer.createElement('div');
        this.renderer.addClass(container, 'search-input-container');
        this.renderer.addClass(container, 'fill-flex');
        this.renderer.insertBefore(this.parentElement, container, this.nativeElement);
        this.renderer.appendChild(container, this.nativeElement);
        this.container = container;

        // 2) Angular-driven updates (Reactive Forms or ngModel)
        if (this.ngControl?.valueChanges) {
          this.valueChangesSub = this.ngControl.valueChanges.subscribe(() => {
            updateHasValue(container);
          });
        } else {
          // 3) Fallback for programmatic .value / [value] when *no* NgControl is attached:
          this.teardownValuePatch = this.patchValueSetter(() => updateHasValue(container));
        }

        // 4) initial sync (handles prefilled values)
        queueMicrotask(() => updateHasValue(container));

        const clearBtn = this.viewContainerRef.createComponent(MdwButton);
        const clearStyle = this.clearStyle();
        if (clearStyle === 'text') {
          // set inner text
          const text = document.createTextNode('Clear');
          (clearBtn.location.nativeElement as HTMLElement)
            .querySelector('.text-content')
            ?.appendChild(text);
        } else {
          clearBtn.setInput('icon', Close_Large);
        }
        (clearBtn.location.nativeElement as HTMLElement).classList.add(
          'mdw-button',
          'mdw-tertiary',
          'small-button',
          'clear-button',
        );
        (clearBtn.location.nativeElement as HTMLElement).addEventListener('click', () => {
          this.nativeElement.value = '';
          updateHasValue(container);
          this.nativeElement.focus();
          this.clear.emit();
        });
        this.renderer.appendChild(container, clearBtn.location.nativeElement);

        const iconRef = this.viewContainerRef.createComponent(MdwIcon);
        iconRef.instance.setIcon(Search);
        this.renderer.appendChild(container, iconRef.location.nativeElement);
        break;
      }
      case 'password': {
        // surround the input with a container div and add a show/hide password button
        const container = this.renderer.createElement('div');
        this.renderer.addClass(container, 'password-input-container');
        this.renderer.addClass(container, 'fill-flex');
        this.renderer.insertBefore(this.parentElement, container, this.nativeElement);
        this.renderer.appendChild(container, this.nativeElement);
        this.container = container;

        const showHideBtn = this.viewContainerRef.createComponent(MdwButton);
        showHideBtn.setInput('icon', View_Outline);
        (showHideBtn.location.nativeElement as HTMLElement).classList.add(
          'mdw-button',
          'mdw-tertiary',
          'small-button',
          'show-hide-button',
        );
        (showHideBtn.location.nativeElement as HTMLElement).addEventListener('click', () => {
          this.nativeElement.type = this.nativeElement.type === 'password' ? 'text' : 'password';
          showHideBtn.setInput(
            'icon',
            this.nativeElement.type === 'password' ? View_Outline : Hide_Outline,
          );
          this.nativeElement.focus();
        });
        this.renderer.appendChild(container, showHideBtn.location.nativeElement);
        break;
      }
    }

    if (this.ngControl && this.ngControl.control) {
      this.control = this.ngControl.control;

      this.statusSubscription = this.control.statusChanges.subscribe(() => {
        this.updateErrorDisplay();
      });

      // Initial display update
      this.updateErrorDisplay();
    }
  }

  ngOnDestroy() {
    this.unlistenInput?.();
    this.valueChangesSub?.unsubscribe?.();
    this.statusSubscription?.unsubscribe?.();
    this.teardownValuePatch?.();

    // The wrapper/label/description/error nodes were created imperatively and
    // inserted into parentElement, so they live outside Angular's view tree —
    // Angular tears down the host input but never these. Remove them here or
    // they accumulate every time the input is re-created (e.g. an empty
    // .search-input-container leaked on each Members-tab switch).
    this.container?.remove();
    this.createdLabel?.remove();
    this.createdDescription?.remove();
    this.inputError?.remove();
  }

  // --- helper to patch the 'value' property so programmatic sets trigger our update ---
  private patchValueSetter(onSet: () => void) {
    const el = this.nativeElement;
    const isTextarea = el.tagName.toLowerCase() === 'textarea';

    const desc = Object.getOwnPropertyDescriptor(
      (isTextarea ? HTMLTextAreaElement : HTMLInputElement).prototype,
      'value',
    );

    const descGet = desc?.get;
    const descSet = desc?.set;

    if (!desc || !descGet || !descSet) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    // Define an own property so assignments go through us
    Object.defineProperty(el, 'value', {
      configurable: true,
      get() {
        return descGet.call(this);
      },
      set(v: string) {
        descSet.call(this, v);
        onSet(); // notify after any programmatic set (incl. Angular [value] binding)
      },
    });

    // Teardown restores prototype getter/setter
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (el as any).value;
    };
  }

  private updateErrorDisplay() {
    this.removeErrorDisplay();

    if (this.control.errors && this.control.dirty) {
      const errorMessage = this.getErrorMessage();
      this.inputError = this.renderer.createElement('div');
      this.renderer.addClass(this.inputError, 'input-error');
      const errorText = this.renderer.createText(errorMessage);
      this.renderer.appendChild(this.inputError, errorText);
      this.renderer.appendChild(this.parentElement, this.inputError);
      this.renderer.addClass(this.nativeElement, 'error');
    }
  }

  private removeErrorDisplay() {
    if (this.inputError?.parentNode) {
      this.renderer.removeChild(this.inputError?.parentNode, this.inputError);
    }

    this.renderer.removeClass(this.nativeElement, 'error');
  }

  private getErrorMessage() {
    const errors = this.control.errors;
    let errorMessage = 'There seems to be an error with this field.';

    if (!errors) {
      return '';
    } else {
      Object.keys(errors).forEach(key => {
        if (this.customErrorMessages()[key]) {
          errorMessage = this.customErrorMessages()[key];
        } else {
          switch (key) {
            case 'required': {
              errorMessage = 'This field is required.';
              break;
            }
            case 'email': {
              errorMessage = 'Please enter a valid email address.';
              break;
            }
          }
        }
      });

      return errorMessage;
    }
  }
}
